import type {
  AggregateExposure,
  AssetGroup,
  BulkTradeRunState,
  Currency,
  DashboardPortfolioReport,
  DisplayDensity,
  Holding,
  ModelAggregation,
  OptimizerState,
  PipelineStage,
  PortfolioReport,
  PortfolioStatus,
  PreFlightCheck,
  PreFlightResult,
  SubTradeRequest,
  ValidationSeverity,
} from '../types';

export const formatMoney = (
  cents: number,
  opts: { decimals?: number; compact?: boolean } = {},
): string => {
  const { decimals = 2, compact = false } = opts;
  const dollars = cents / 100;
  if (compact && Math.abs(dollars) >= 1_000_000)
    return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (compact && Math.abs(dollars) >= 1_000)
    return `$${(dollars / 1_000).toFixed(0)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(dollars);
};

/* ───────────────────────────────────────────────── */
/*  Formatting helpers (from tradeBlotter, etc.)     */
/* ───────────────────────────────────────────────── */

export const fmtPct = (pct: number): string => `${pct.toFixed(1)}%`;
export const fmtQty = (qty: number): string =>
  qty === 0 ? '—' : qty.toLocaleString('en-US', { maximumFractionDigits: 2 });
export const fmtFx = (rate: number): string =>
  rate === 1 ? '—' : rate.toFixed(4);

export const classifyGroup = (h: {
  isCashEquivalent?: boolean;
  financialProduct?: {
    isCash?: boolean;
    name?: string;
    translatedName?: { en?: string };
  };
}): AssetGroup => {
  if (h.isCashEquivalent || h.financialProduct?.isCash) return 'CASH';
  const name = (
    h.financialProduct?.name ||
    h.financialProduct?.translatedName?.en ||
    ''
  ).toUpperCase();
  if (
    name.includes('BOND') ||
    name.includes('FIXED INCOME') ||
    name.includes('AGGREGATE BOND') ||
    name.includes('TREASURY') ||
    name.includes('CREDIT')
  )
    return 'FIXED_INCOME';
  return 'EQUITIES';
};

export const buildGrid = (
  saCount: number,
  multiSA: boolean,
  showSAPost: boolean,
): string => {
  const pos = 'minmax(120px,1.2fr) 96px 48px 48px 52px';
  const saPer =
    multiSA && showSAPost
      ? 'minmax(130px,1fr) 76px'
      : multiSA
        ? 'minmax(130px,1fr)'
        : 'minmax(120px,0.8fr)';
  const sa = Array(saCount).fill(saPer).join(' ');
  const result = 'minmax(90px,1fr) 52px';
  return `${pos} ${sa} ${result}`;
};

/* ───────────────────────────────────────────────── */
/*  Optimizer workflow helpers                       */
/* ───────────────────────────────────────────────── */

export const OPTIMIZER_STAGE_GROUPS: {
  label: string;
  states: OptimizerState[];
}[] = [
  {
    label: 'Analyzing',
    states: [
      'INITIATED',
      'OPTIMAL_PORTFOLIOS_GENERATING',
      'OPTIMAL_PORTFOLIOS_GENERATED',
    ],
  },
  { label: 'Sales', states: ['SOLVING_FOR_REDEMPTIONS', 'REDEMPTIONS_SOLVED'] },
  { label: 'Purchases', states: ['SOLVING_FOR_PURCHASES', 'PURCHASES_SOLVED'] },
  { label: 'Rebalancing', states: ['REBALANCING', 'REBALANCED'] },
  { label: 'Tax Opt.', states: ['TAX_OPTIMIZING', 'TAX_OPTIMIZED'] },
  { label: 'Trades', states: ['GENERATING_ALLOCATIONS'] },
  { label: 'Complete', states: ['COMPLETED'] },
];

export const getStageIndex = (state: OptimizerState): number => {
  for (let i = 0; i < OPTIMIZER_STAGE_GROUPS.length; i++) {
    if (OPTIMIZER_STAGE_GROUPS[i].states.includes(state)) return i;
  }
  return -1;
};

const REGISTERED_ACCOUNT_TYPES = [
  'TFSA',
  'RRSP',
  'RESP',
  'LIRA',
  'LIF',
  'RRIF',
  'RDSP',
  'FHSA',
  'DPSP',
];

export const parseAccountType = (account: string): string => {
  const parts = account.split(' · ');
  return parts.length > 1 ? parts[parts.length - 1].trim() : '';
};

export const isRegistered = (acctType: string): boolean =>
  REGISTERED_ACCOUNT_TYPES.some((r) => acctType.toUpperCase().includes(r));

export const getClientName = (r: {
  object?: {
    user?: { firstName?: string; lastName?: string };
    account?: { user?: { firstName?: string; lastName?: string } };
  };
}): string => {
  const user = r.object?.user || r.object?.account?.user;
  if (!user) return '';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim();
};

export const getTriggers = (r: {
  driftRebalanceRequired?: boolean;
  transferRebalanceRequired?: boolean;
  liquidateRebalanceRequired?: boolean;
}): string[] => {
  const triggers: string[] = [];
  if (r.driftRebalanceRequired) triggers.push('Drift');
  if (r.transferRebalanceRequired) triggers.push('Transfer');
  if (r.liquidateRebalanceRequired) triggers.push('Liquidate');
  return triggers;
};

export type HoldingDiff = {
  ticker: string;
  name: string;
  currentPct: number;
  targetPct: number;
  deltaPct: number;
  isCash: boolean;
};

export const computeHoldingDiffs = (report: {
  holdings?: Array<{
    valueCents?: number;
    expectedPercentage?: number;
    financialProduct?: {
      ticker?: string;
      translatedName?: { en?: string };
      name?: string;
      isCash?: boolean;
    };
  }>;
  marketValueCents?: number;
}): HoldingDiff[] => {
  const holdings = report.holdings || [];
  const mv = report.marketValueCents || 1;
  return holdings
    .map((h) => {
      const currentPct = ((h.valueCents || 0) / mv) * 100;
      const targetPct = (h.expectedPercentage || 0) * 100;
      return {
        ticker: h.financialProduct?.ticker || '???',
        name:
          h.financialProduct?.translatedName?.en ||
          h.financialProduct?.name ||
          '',
        currentPct,
        targetPct,
        deltaPct: currentPct - targetPct,
        isCash: !!h.financialProduct?.isCash,
      };
    })
    .filter((d) => !d.isCash && Math.abs(d.deltaPct) > 0.25)
    .sort((a, b) => Math.abs(b.deltaPct) - Math.abs(a.deltaPct));
};

/* ───────────────────────────────────────────────── */
/*  Bulk trade run workflow helpers                  */
/* ───────────────────────────────────────────────── */

const BULK_TRADE_STEPS = [
  { key: 'review' as const, label: 'Review', num: 1 },
  { key: 'update' as const, label: 'Update', num: 2 },
  { key: 'allocations' as const, label: 'Allocations', num: 3 },
  { key: 'complete' as const, label: 'Complete', num: 4 },
];

export type BulkTradeViewKey = (typeof BULK_TRADE_STEPS)[number]['key'];

export const getActiveStep = (state: string): BulkTradeViewKey => {
  if (state === 'BULK_TRADES_INITIATED') return 'review';
  if (state === 'BULK_TRADES_COMPLETED' || state === 'COMPLETED_PENDING_PTFS')
    return 'update';
  if (
    state === 'ALLOCATIONS_INITIATED' ||
    state === 'ALLOCATIONS_REQUESTED' ||
    state === 'ALLOCATIONS_COMPLETED'
  )
    return 'allocations';
  if (state === 'COMPLETED') return 'complete';
  return 'review';
};

export const getStepIndex = (key: BulkTradeViewKey): number =>
  BULK_TRADE_STEPS.findIndex((s) => s.key === key);

/* ───────────────────────────────────────────────── */
/*  Time & UI helpers                                */
/* ───────────────────────────────────────────────── */

export const relativeTime = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export type PriorityKey = 'urgent' | 'review' | 'ok';

export const classifyPriority = (
  report: PortfolioReport,
): { priority: PriorityKey; rank: number } => {
  const drift = calculateDrift(report) ?? 0;
  if (
    (report.driftRebalanceRequired && drift > 5) ||
    report.liquidateRebalanceRequired ||
    report.transferRebalanceRequired
  )
    return { priority: 'urgent', rank: 0 };
  if (report.driftRebalanceRequired) return { priority: 'review', rank: 1 };
  return { priority: 'ok', rank: 2 };
};

export const getPriorityChips = (
  counts: { urgent: number; review: number; ok: number },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sys: { color: any },
): Array<{
  key: 'ALL' | 'urgent' | 'review' | 'ok';
  label: string;
  color: string;
  bg: string;
  count: number;
}> => [
  {
    key: 'ALL' as const,
    label: 'All',
    color: sys.color.onSurfaceVariant,
    bg: sys.color.neutralHover,
    count: counts.urgent + counts.review + counts.ok,
  },
  {
    key: 'urgent' as const,
    label: String(counts.urgent),
    color: sys.color.negative,
    bg: sys.color.negativeVariant,
    count: counts.urgent,
  },
  {
    key: 'review' as const,
    label: String(counts.review),
    color: sys.color.warning,
    bg: sys.color.warningVariant,
    count: counts.review,
  },
  {
    key: 'ok' as const,
    label: String(counts.ok),
    color: sys.color.positive,
    bg: sys.color.positiveVariant,
    count: counts.ok,
  },
];

const DEFAULT_ASSET_CLASS = 'Other';

export const getAssetClassName = (e: AggregateExposure): string =>
  e.primaryAssetClassName || DEFAULT_ASSET_CLASS;

export const OPTIMIZER_STATE_LABELS: Record<OptimizerState, string> = {
  INITIATED: 'Starting...',
  OPTIMAL_PORTFOLIOS_GENERATING: 'Analyzing portfolios...',
  OPTIMAL_PORTFOLIOS_GENERATED: 'Analysis complete',
  SOLVING_FOR_REDEMPTIONS: 'Calculating sales...',
  REDEMPTIONS_SOLVED: 'Sales calculated',
  SOLVING_FOR_PURCHASES: 'Calculating buys...',
  PURCHASES_SOLVED: 'Buys calculated',
  REBALANCING: 'Rebalancing allocations...',
  REBALANCED: 'Rebalanced',
  TAX_OPTIMIZING: 'Optimizing for taxes...',
  TAX_OPTIMIZED: 'Ready to generate trades',
  GENERATING_ALLOCATIONS: 'Generating trade instructions...',
  COMPLETED: 'Complete',
  CANCELED: 'Canceled',
  FAILED: 'Failed',
};

export const BULK_TRADE_RUN_STATE_LABELS: Record<BulkTradeRunState, string> = {
  GENERATING: 'Preparing trades...',
  BULK_TRADES_INITIATED: 'Ready for review',
  BULK_TRADES_FILES_GENERATING: 'Generating trade files...',
  BULK_TRADES_REQUESTED: 'Processing...',
  BULK_TRADES_COMPLETED: 'Ready to allocate',
  ALLOCATIONS_INITIATED: 'Preparing allocations...',
  ALLOCATIONS_REQUESTED: 'Processing allocations...',
  ALLOCATIONS_COMPLETED: 'Allocations ready',
  COMPLETED: 'Complete',
  COMPLETED_PENDING_PTFS: 'PTF trades pending',
  CANCELED: 'Canceled',
};

export const getOptimizerProgress = (state: OptimizerState): number => {
  const progressMap: Record<string, number> = {
    INITIATED: 5,
    OPTIMAL_PORTFOLIOS_GENERATING: 20,
    OPTIMAL_PORTFOLIOS_GENERATED: 30,
    SOLVING_FOR_REDEMPTIONS: 40,
    REDEMPTIONS_SOLVED: 50,
    SOLVING_FOR_PURCHASES: 60,
    PURCHASES_SOLVED: 70,
    REBALANCING: 80,
    REBALANCED: 85,
    TAX_OPTIMIZING: 90,
    TAX_OPTIMIZED: 95,
    GENERATING_ALLOCATIONS: 98,
    COMPLETED: 100,
  };
  return progressMap[state] || 0;
};

export const getBulkTradeRunProgress = (state: BulkTradeRunState): number => {
  const progressMap: Record<string, number> = {
    GENERATING: 10,
    BULK_TRADES_INITIATED: 25,
    BULK_TRADES_FILES_GENERATING: 40,
    BULK_TRADES_REQUESTED: 55,
    BULK_TRADES_COMPLETED: 70,
    ALLOCATIONS_INITIATED: 80,
    ALLOCATIONS_REQUESTED: 90,
    ALLOCATIONS_COMPLETED: 95,
    COMPLETED: 100,
  };
  return progressMap[state] || 0;
};

export const getBulkTradeRunStages = (
  currentState: BulkTradeRunState,
): PipelineStage[] => {
  const stages = [
    { states: ['GENERATING'], label: 'Generating' },
    { states: ['BULK_TRADES_INITIATED'], label: 'Review Trades' },
    {
      states: ['BULK_TRADES_FILES_GENERATING', 'BULK_TRADES_REQUESTED'],
      label: 'Processing',
    },
    { states: ['BULK_TRADES_COMPLETED'], label: 'Update Trades' },
    {
      states: ['ALLOCATIONS_INITIATED', 'ALLOCATIONS_REQUESTED'],
      label: 'Allocations',
    },
    {
      states: ['ALLOCATIONS_COMPLETED', 'COMPLETED', 'COMPLETED_PENDING_PTFS'],
      label: 'Complete',
    },
  ];
  const currentIdx = stages.findIndex((stage) =>
    stage.states.includes(currentState),
  );
  return stages.map((stage, idx) => ({
    label: stage.label,
    states: stage.states,
    active: idx === currentIdx,
    completed: idx < currentIdx,
  }));
};

export const getNextActionLabel = (state: BulkTradeRunState): string => {
  const actionMap: Record<string, string> = {
    GENERATING: 'Generating...',
    BULK_TRADES_INITIATED: 'Review Trades',
    BULK_TRADES_FILES_GENERATING: 'Generating files...',
    BULK_TRADES_REQUESTED: 'Processing...',
    BULK_TRADES_COMPLETED: 'Update Trades',
    ALLOCATIONS_INITIATED: 'Preparing allocations...',
    ALLOCATIONS_REQUESTED: 'Processing allocations...',
    ALLOCATIONS_COMPLETED: 'Review Allocations',
    COMPLETED_PENDING_PTFS: 'Review PTF Trades',
  };
  return actionMap[state] || 'View Details';
};

export const calculateDrift = (report: PortfolioReport): number | null => {
  if (!report.holdings || report.holdings.length === 0) return null;
  return report.holdings.reduce((sum: number, holding: Holding) => {
    const currentPct =
      ((holding.valueCents || 0) / (report.marketValueCents || 1)) * 100;
    const targetPct = (holding.expectedPercentage || 0) * 100;
    return sum + Math.abs(currentPct - targetPct);
  }, 0);
};

export const getPortfolioName = (
  report: PortfolioReport | DashboardPortfolioReport,
): string => {
  const obj = report.object;
  if (!obj) return 'Portfolio';
  if (report.modelPortfolioType === 'GOAL') {
    const o = obj as {
      name?: string;
      user?: { firstName?: string; lastName?: string };
    };
    const user = o?.user;
    if (user?.firstName)
      return `${user.firstName} ${user.lastName || ''}`.trim();
    return o?.name || 'Goal Portfolio';
  }
  const o = obj as {
    account?: {
      type?: string;
      user?: { firstName?: string; lastName?: string };
    };
    goal?: { name?: string };
  };
  const user = o.account?.user;
  const acctType = o.account?.type;
  const goalName = o.goal?.name;
  const name = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : '';
  const suffix = acctType ? ` · ${acctType}` : '';
  const goalSuffix = goalName ? ` · ${goalName}` : '';
  return name + suffix + goalSuffix || 'Portfolio';
};

export const getPortfolioStatus = (
  report: PortfolioReport,
): PortfolioStatus => {
  if (report.pendingSubTradeRequests > 0)
    return { label: 'Trading Active', severity: 'info' };
  if (report.liquidateRebalanceRequired)
    return { label: 'Liquidation Required', severity: 'error' };
  if (report.transferRebalanceRequired)
    return { label: 'Transfer Pending', severity: 'warning' };
  if (report.driftRebalanceRequired)
    return { label: 'Drift Detected', severity: 'warning' };
  return { label: 'Balanced', severity: 'success' };
};

export const getStatusCode = (report: PortfolioReport): string =>
  report.liquidateRebalanceRequired
    ? 'LIQ'
    : report.transferRebalanceRequired
      ? 'XFER'
      : report.driftRebalanceRequired
        ? 'DRIFT'
        : report.pendingSubTradeRequests > 0
          ? 'PEND'
          : 'OK';

export const isBuyType = (type: string): boolean =>
  type === 'BUY' || type === 'BUY_PENDING_CONTRIBUTION';
export const isSellType = (type: string, isSellAll?: boolean): boolean =>
  type === 'SELL' || type === 'SELL_PENDING_WITHDRAW' || !!isSellAll;
export const calculateQuantity = (
  moneyAllocatedCents: number,
  priceCents: number,
): number => (!priceCents ? 0 : Math.abs(moneyAllocatedCents) / priceCents);

export const runPreFlightChecks = (
  report: PortfolioReport,
  pendingTrades: SubTradeRequest[],
): PreFlightResult => {
  const checks: PreFlightCheck[] = [];
  const totalBuyCents = pendingTrades
    .filter((t) => isBuyType(t.type))
    .reduce((sum, t) => sum + Math.abs(t.moneyAllocatedCents), 0);

  checks.push(
    totalBuyCents > report.cashAvailableForTradeCents
      ? {
          name: 'Cash Sufficiency',
          severity: 'error',
          message: `Buys ($${(totalBuyCents / 100).toFixed(2)}) exceed available cash ($${(report.cashAvailableForTradeCents / 100).toFixed(2)})`,
        }
      : {
          name: 'Cash Sufficiency',
          severity: 'pass',
          message: 'Available cash covers all pending buys',
        },
  );

  const drift = calculateDrift(report);
  checks.push(
    drift && drift > 10
      ? {
          name: 'Drift Level',
          severity: 'error',
          message: `Portfolio drift at ${drift.toFixed(1)}% — significantly out of tolerance`,
        }
      : drift && drift > 5
        ? {
            name: 'Drift Level',
            severity: 'warning',
            message: `Portfolio drift at ${drift.toFixed(1)}% — above threshold`,
          }
        : {
            name: 'Drift Level',
            severity: 'pass',
            message: 'Drift within tolerance',
          },
  );

  const blockedTrades = pendingTrades.filter(
    (t) =>
      t.blockedUntilAccountActive ||
      t.blockedUntilSubAccountActive ||
      (t.blockedUntil && new Date(t.blockedUntil) > new Date()),
  );
  checks.push(
    blockedTrades.length > 0
      ? {
          name: 'Blocked Trades',
          severity: 'warning',
          message: `${blockedTrades.length} trade(s) blocked — account inactive or scheduled`,
        }
      : {
          name: 'Blocked Trades',
          severity: 'pass',
          message: 'No blocked trades',
        },
  );

  checks.push(
    report.unverifiedSubTradeRequests > 0
      ? {
          name: 'Verification',
          severity: 'warning',
          message: `${report.unverifiedSubTradeRequests} unverified trade(s) pending review`,
        }
      : {
          name: 'Verification',
          severity: 'pass',
          message: 'All trades verified',
        },
  );

  const concentratedHoldings = report.holdings.filter((h) => {
    if (h.financialProduct?.isCash) return false;
    return (h.valueCents / (report.marketValueCents || 1)) * 100 > 25;
  });
  const tickers = concentratedHoldings
    .map((h) => h.financialProduct?.ticker)
    .join(', ');
  checks.push(
    concentratedHoldings.length > 0
      ? {
          name: 'Concentration',
          severity: 'warning',
          message: `${tickers} exceeds 25% concentration`,
        }
      : {
          name: 'Concentration',
          severity: 'pass',
          message: 'No concentration issues',
        },
  );

  const overallSeverity: ValidationSeverity = checks.some(
    (c) => c.severity === 'error',
  )
    ? 'error'
    : checks.some((c) => c.severity === 'warning')
      ? 'warning'
      : 'pass';
  return { portfolioId: report.id, checks, overallSeverity };
};

export const calculateAggregateExposure = (
  reports: PortfolioReport[],
): AggregateExposure[] => {
  const exposureMap = new Map<string, AggregateExposure>();
  const totalAumCents = reports.reduce(
    (sum, r) => sum + (r.marketValueCents || 0),
    0,
  );

  reports.forEach((report: any) => {
    report.holdings?.forEach((holding: any) => {
      if (holding.financialProduct?.isCash) return;
      const ticker = holding.financialProduct?.ticker;
      if (!ticker) return;
      const fpId = holding.financialProduct?.id || '';
      const subAccountId =
        report.subAccounts?.[0]?.subAccount?.id || report.object?.id || '';
      const portfolioEntry = {
        portfolioId: report.id,
        portfolioReportId: report.id,
        portfolioName: getPortfolioName(report),
        subAccountId,
        modelPortfolioType: report.modelPortfolioType || 'SUB_ACCOUNT',
        objectId: report.object?.id || '',
        valueCents: holding.valueCents,
        quantity: holding.quantity,
        percentOfPortfolio:
          ((holding.valueCents || 0) / (report.marketValueCents || 1)) * 100,
        expectedPercentage: holding.expectedPercentage || 0,
        expectedValueCents: holding.expectedValueCents || 0,
        portfolioMarketValueCents: report.marketValueCents || 0,
        cashAvailableForTradeCents: report.cashAvailableForTradeCents || 0,
      };
      const existing = exposureMap.get(ticker);
      if (existing) {
        existing.totalValueCents += holding.valueCents || 0;
        existing.totalQuantity += holding.quantity || 0;
        existing.portfolioCount += 1;
        existing.percentOfTotalAum =
          (existing.totalValueCents / (totalAumCents || 1)) * 100;
        existing.portfolios.push(portfolioEntry);
      } else {
        exposureMap.set(ticker, {
          ticker,
          productName:
            holding.financialProduct?.translatedName?.en ||
            holding.financialProduct?.name ||
            ticker,
          financialProductId: fpId,
          currency: holding.currency,
          totalValueCents: holding.valueCents || 0,
          totalQuantity: holding.quantity || 0,
          portfolioCount: 1,
          percentOfTotalAum:
            ((holding.valueCents || 0) / (totalAumCents || 1)) * 100,
          primaryAssetClassKey:
            holding.financialProduct?.primaryAssetClass?.key,
          primaryAssetClassName:
            holding.financialProduct?.primaryAssetClass?.translatedName?.en,
          portfolios: [portfolioEntry],
        });
      }
    });
  });
  return Array.from(exposureMap.values()).sort(
    (a, b) => b.totalValueCents - a.totalValueCents,
  );
};

export const calculateModelAggregation = (
  reports: PortfolioReport[],
): ModelAggregation[] => {
  const modelMap = new Map<string, ModelAggregation>();
  reports.forEach((report) => {
    const modelId = report.modelPortfolio?.id || '__unassigned__';
    const modelName = report.modelPortfolio?.translatedName?.en || 'Unassigned';
    const existing = modelMap.get(modelId);
    const needsRebalance =
      report.driftRebalanceRequired ||
      report.liquidateRebalanceRequired ||
      report.transferRebalanceRequired;
    if (existing) {
      existing.portfolioCount += 1;
      existing.totalMarketValueCents += report.marketValueCents || 0;
      existing.totalCashCents += report.cashCents || 0;
      if (needsRebalance) existing.needsRebalanceCount += 1;
      existing.pendingTradesCount += report.pendingSubTradeRequests || 0;
      if (
        report.baseCurrency &&
        !existing.currencies.includes(report.baseCurrency)
      )
        existing.currencies.push(report.baseCurrency);
      existing.portfolios.push(report);
    } else {
      modelMap.set(modelId, {
        modelId,
        modelName,
        portfolioCount: 1,
        totalMarketValueCents: report.marketValueCents || 0,
        totalCashCents: report.cashCents || 0,
        avgDriftPct: 0,
        needsRebalanceCount: needsRebalance ? 1 : 0,
        pendingTradesCount: report.pendingSubTradeRequests || 0,
        currencies: report.baseCurrency ? [report.baseCurrency] : [],
        portfolios: [report],
      });
    }
  });
  const result = Array.from(modelMap.values());
  result.forEach((model) => {
    const drifts = model.portfolios
      .map((r) => calculateDrift(r))
      .filter((d): d is number => d !== null);
    model.avgDriftPct =
      drifts.length > 0 ? drifts.reduce((s, d) => s + d, 0) / drifts.length : 0;
  });
  return result.sort(
    (a, b) => b.totalMarketValueCents - a.totalMarketValueCents,
  );
};

export const DENSITY_CONFIG: Record<
  DisplayDensity,
  {
    rowPadding: number;
    fontSize: string;
    cellPadding: string;
    headerHeight: number;
  }
> = {
  comfortable: {
    rowPadding: 12,
    fontSize: '13px',
    cellPadding: '12px 16px',
    headerHeight: 48,
  },
  compact: {
    rowPadding: 6,
    fontSize: '12px',
    cellPadding: '6px 12px',
    headerHeight: 36,
  },
  'ultra-compact': {
    rowPadding: 2,
    fontSize: '11px',
    cellPadding: '2px 8px',
    headerHeight: 28,
  },
};

/* ───────────────────────────────────────────────── */
/*  Color helpers                                    */
/* ───────────────────────────────────────────────── */

export type SemanticColorMap = {
  positive?: string;
  negative?: string;
  warning?: string;
  primary?: string;
  onSurface?: string;
  onSurfaceVariant?: string;
  positiveVariant?: string;
  negativeVariant?: string;
  warningVariant?: string;
  primaryVariant?: string;
  neutralHover?: string;
  outlineVariant?: string;
};

export type SysColors = SemanticColorMap & Record<string, string | undefined>;

export const tradeSideColor = (c: SemanticColorMap, isBuy: boolean): string =>
  (isBuy ? c.positive : c.negative) ?? '';

export const valueColor = (c: SemanticColorMap, val: number): string =>
  (val > 0 ? c.positive : val < 0 ? c.negative : c.onSurface) ?? '';

export const driftColor = (c: SemanticColorMap, abs: number): string =>
  (abs > 3 ? c.negative : abs > 1 ? c.warning : c.positive) ?? '';

export const weightColor = (c: SemanticColorMap, pct: number): string =>
  (pct > 15 ? c.negative : pct > 8 ? c.warning : c.primary) ?? '';

export const hexAlpha = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

export const generateCsv = (
  headers: string[],
  rows: (string | number)[][],
): string => {
  const escapeCsv = (val: string | number): string => {
    const str = String(val);
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };
  const headerRow = headers.map(escapeCsv).join(',');
  const dataRows = rows.map((row) => row.map(escapeCsv).join(','));
  return [headerRow, ...dataRows].join('\n');
};

export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const exportTradesToCsv = (trades: SubTradeRequest[]): void => {
  const headers = [
    'Trade ID',
    'State',
    'Side',
    'Ticker',
    'Product',
    'Qty (Est)',
    'Amount',
    'Currency',
    'Client',
    'Account Type',
    'Settlement Date',
    'Verified',
    'Verified By',
    'Blocked',
  ];
  const rows = trades.map((t) => [
    t.id,
    t.state,
    isBuyType(t.type) ? 'BUY' : 'SELL',
    t.financialProduct?.ticker || '',
    t.financialProduct?.translatedName?.en || '',
    t.financialProduct?.currentPriceCents
      ? (
          Math.abs(t.moneyAllocatedCents) / t.financialProduct.currentPriceCents
        ).toFixed(4)
      : '',
    (Math.abs(t.moneyAllocatedCents) / 100).toFixed(2),
    t.currency || '',
    t.subAccount?.account?.user?.firstName || '',
    t.subAccount?.account?.type || '',
    t.settlementDate || '',
    t.verifiedAt ? 'Yes' : 'No',
    t.verifiedByOrganizationUser
      ? `${t.verifiedByOrganizationUser.firstName} ${t.verifiedByOrganizationUser.lastName}`
      : '',
    t.blockedUntilAccountActive || t.blockedUntilSubAccountActive
      ? 'Yes'
      : 'No',
  ]);
  const csv = generateCsv(headers, rows);
  downloadCsv(
    csv,
    `trade-requests-${new Date().toISOString().split('T')[0]}.csv`,
  );
};

/* ───────────────────────────────────────────────── */
/*  Optimizer Decision Intelligence — narratives    */
/* ───────────────────────────────────────────────── */

type DecisionSummary = {
  totalPortfolios: number;
  driftCount: number;
  transferCount: number;
  liquidateCount: number;
  reports: any[];
};

type TradeSummary = {
  totalBuys: number;
  totalSells: number;
  totalBuyCents: number;
  totalSellCents: number;
  registeredSellPct: number;
};

const pluralize = (n: number, singular: string, plural?: string) =>
  n === 1 ? `${n} ${singular}` : `${n} ${plural ?? `${singular}s`}`;

export const buildExecutiveSummary = (
  decision: DecisionSummary,
  tradeSummary: TradeSummary,
): string[] => {
  const lines: string[] = [];
  if (decision.totalPortfolios === 0) return lines;

  const totalAumCents = decision.reports.reduce(
    (sum: number, r: any) => sum + (r.marketValueCents || 0),
    0,
  );

  const reasons: string[] = [];
  if (decision.driftCount > 0)
    reasons.push(
      `${pluralize(decision.driftCount, 'portfolio')} with allocation drift`,
    );
  if (decision.transferCount > 0)
    reasons.push(
      `${pluralize(decision.transferCount, 'portfolio')} with undeployed cash`,
    );
  if (decision.liquidateCount > 0)
    reasons.push(
      `${pluralize(decision.liquidateCount, 'portfolio')} needing liquidation`,
    );

  lines.push(
    `Analyzed ${pluralize(decision.totalPortfolios, 'portfolio')} worth ${formatMoney(totalAumCents, { compact: true })} total.` +
      (reasons.length > 0 ? ` Found ${reasons.join(', ')}.` : ''),
  );

  const totalTrades = tradeSummary.totalBuys + tradeSummary.totalSells;
  if (totalTrades > 0) {
    const parts = [];
    if (tradeSummary.totalBuys > 0)
      parts.push(
        `${pluralize(tradeSummary.totalBuys, 'buy')} (${formatMoney(tradeSummary.totalBuyCents, { compact: true })})`,
      );
    if (tradeSummary.totalSells > 0)
      parts.push(
        `${pluralize(tradeSummary.totalSells, 'sell')} (${formatMoney(tradeSummary.totalSellCents, { compact: true })})`,
      );
    lines.push(
      `Generated ${pluralize(totalTrades, 'trade')}: ${parts.join(', ')}.`,
    );
  }

  if (tradeSummary.totalSells > 0 && tradeSummary.registeredSellPct > 0) {
    const pct = Math.round(tradeSummary.registeredSellPct);
    if (pct >= 50) {
      lines.push(`${pct}% of sell activity in tax-sheltered accounts.`);
    } else if (pct > 0) {
      lines.push(
        `${pct}% of sell activity in tax-sheltered accounts; ${100 - pct}% may trigger capital gains.`,
      );
    }
  }

  const netCashFlowCents =
    tradeSummary.totalSellCents - tradeSummary.totalBuyCents;
  if (Math.abs(netCashFlowCents) > 10000) {
    if (netCashFlowCents > 0) {
      lines.push(
        `Net raising ${formatMoney(netCashFlowCents, { compact: true })} in cash across portfolios.`,
      );
    } else {
      lines.push(
        `Net deploying ${formatMoney(Math.abs(netCashFlowCents), { compact: true })} of cash into target allocations.`,
      );
    }
  }

  return lines;
};

export const exportPortfoliosToCsv = (reports: PortfolioReport[]): void => {
  const headers = [
    'Portfolio ID',
    'Name',
    'Type',
    'Model Portfolio',
    'Market Value',
    'Cash Available',
    'Cash %',
    'Target Cash %',
    'Status',
    'Drift Required',
    'Pending Trades',
    'Last Optimized',
  ];
  const rows = reports.map((r) => [
    r.id,
    getPortfolioName(r),
    r.modelPortfolioType,
    r.modelPortfolio?.translatedName?.en || '',
    (r.marketValueCents / 100).toFixed(2),
    (r.cashAvailableForTradeCents / 100).toFixed(2),
    (
      ((r.cashAvailableForTradeCents || 0) / (r.marketValueCents || 1)) *
      100
    ).toFixed(1),
    (r.expectedCashPercentage * 100).toFixed(1),
    getPortfolioStatus(r).label,
    r.driftRebalanceRequired ? 'Yes' : 'No',
    r.pendingSubTradeRequests,
    r.lastOptimizedBy?.createdAt || 'Never',
  ]);
  const csv = generateCsv(headers, rows);
  downloadCsv(
    csv,
    `portfolio-reports-${new Date().toISOString().split('T')[0]}.csv`,
  );
};
